import { useEffect, useState } from 'react'
import CombinedWizard from '../features/scholarships/CombinedWizard'
import CombinedResultsPage from './CombinedResultsPage'
import { matchScholarships } from '../services/gemini/matcher'
import { SharedProfile, toScholarshipProfile } from '../lib/sharedProfile'
import { MatchedScholarship } from '../features/scholarships/types'
import { getFromChromeStorage, setInChromeStorage } from '../lib/chromeStorage'

type SelectedScrapedProgram = {
    id: string
    recordId: string
    url: string
    title: string
    school: string | null
    program: string
    tuition: number
    timestamp: number
    source: 'scraped'
}

const SELECTED_PROGRAM_KEY = 'selectedScrapedProgram'
const USER_FUNDING_PROFILE_KEY = 'userFundingProfile'
const LAST_EXTENSION_PROFILE_KEY = 'lastExtensionProfile'
const LAST_EXTENSION_SCHOLARSHIP_RESULTS_KEY = 'lastExtensionScholarshipResults'
const LAST_EXTENSION_SUBMISSION_AT_KEY = 'lastExtensionSubmissionAt'

export default function ExtensionInfoPage() {
    const [selectedProgram, setSelectedProgram] = useState<SelectedScrapedProgram | null>(null)
    const [loadingSelection, setLoadingSelection] = useState(true)

    const [results, setResults] = useState<MatchedScholarship[] | null>(null)
    const [profile, setProfile] = useState<SharedProfile | null>(null)
    const [loading, setLoading] = useState(false)
    const [loadingMessage, setLoadingMessage] = useState('')

    useEffect(() => {
        const loadSelectedProgram = async () => {
            try {
                const stored = await getFromChromeStorage<SelectedScrapedProgram>(SELECTED_PROGRAM_KEY)
                setSelectedProgram(stored)
            } catch (error) {
                console.error('failed to load selected scraped program', error)
            } finally {
                setLoadingSelection(false)
            }
        }

        loadSelectedProgram()
    }, [])

    const handleComplete = async (shared: SharedProfile) => {
        setProfile(shared)
        setLoading(true)
        setLoadingMessage('Searching scholarship sources...')

        const t1 = window.setTimeout(() => setLoadingMessage('Checking your eligibility...'), 4000)
        const t2 = window.setTimeout(() => setLoadingMessage('Almost done...'), 10000)

        try {
            let scholarships: MatchedScholarship[] = []

            try {
                scholarships = await matchScholarships(toScholarshipProfile(shared))
            } catch (error) {
                console.error('scholarship matching failed', error)
                scholarships = []
            }

            await setInChromeStorage(USER_FUNDING_PROFILE_KEY, {
                age: shared.age,
                ontarioResident: shared.ontarioResident,
                status: shared.status,
                worked24MonthsInARow: shared.worked24MonthsInARow,
                yearsOutOfHighSchool: shared.yearsOutOfHighSchool,
                programLevel: shared.programLevel,
                studyLoad: shared.studyLoad,
                studyWeeks: shared.studyWeeks,
                numberOfTerms: shared.numberOfTerms,
                booksSupplies: shared.booksSupplies,
                livingArrangement: shared.livingArrangement,
                actualMonthlyRent: shared.actualMonthlyRent ?? null,
                studentIncome: shared.studentIncome,
                studentAssets: shared.studentAssets,
                parentIncome: shared.parentIncome,
                incomeMax: shared.incomeMax,
                demographicFlags: shared.demographicFlags,
                fixedContributionWaived: shared.fixedContributionWaived,
                hasDependants: shared.hasDependants,
                updatedAt: Date.now(),
                source: 'extension_profile',
            })

            await setInChromeStorage(LAST_EXTENSION_PROFILE_KEY, shared)
            await setInChromeStorage(LAST_EXTENSION_SCHOLARSHIP_RESULTS_KEY, scholarships)
            await setInChromeStorage(LAST_EXTENSION_SUBMISSION_AT_KEY, Date.now())

            setResults(scholarships)
        } finally {
            clearTimeout(t1)
            clearTimeout(t2)
            setLoading(false)
        }
    }

    const hasValidDetectedSelection =
        !!selectedProgram?.program &&
        selectedProgram?.tuition !== null &&
        selectedProgram?.tuition !== undefined

    if (loadingSelection) {
        return (
            <div className="min-h-screen bg-[#f1f5f9] flex items-center justify-center px-4">
                <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm px-8 py-8 flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-slate-500">Loading detected program...</p>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f1f5f9] flex items-center justify-center px-4">
                <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm px-8 py-8 flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-slate-600 text-sm">{loadingMessage}</p>
                    <p className="text-slate-400 text-xs">
                        Calculating your funding and scholarship results...
                    </p>
                </div>
            </div>
        )
    }

    if (results !== null && profile !== null) {
        return <CombinedResultsPage profile={profile} scholarships={results} />
    }

    if (!hasValidDetectedSelection || !selectedProgram) {
        return (
            <div className="min-h-screen bg-[#f1f5f9] flex items-center justify-center px-4 py-10">
                <div className="max-w-2xl w-full rounded-[28px] overflow-hidden border border-slate-200 bg-white shadow-sm">
                    <div className="bg-[#1e293b] px-6 py-5">
                        <h1 className="text-2xl font-bold text-white">find your funding</h1>
                        <p className="text-sm text-slate-300 mt-1">
                            no tuition selection found
                        </p>
                    </div>

                    <div className="px-6 py-6">
                        <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                            open a tuition fee page, wait for the detected program list, and click a
                            program-tuition pair to continue.
                        </div>

                        <div className="mt-4 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                                what happens next
                            </p>
                            <ul className="space-y-2 text-sm text-slate-700">
                                <li>1. go to a tuition or program fee page</li>
                                <li>2. click one of the detected program and tuition pairs</li>
                                <li>3. come back here and fill in the rest of your profile once</li>
                                <li>4. after that, future tuition pages can go straight to the final calculation</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const detectedProgram = selectedProgram

    return (
        <div className="min-h-screen bg-[#f1f5f9] py-10">
            <div className="max-w-4xl mx-auto px-4">
                <div className="mb-8 rounded-[28px] overflow-hidden border border-slate-200 bg-white shadow-sm">
                    <div className="bg-[#1e293b] px-6 py-5">
                        <h1 className="text-2xl font-bold text-white">find your funding</h1>
                        <p className="text-sm text-slate-300 mt-1">
                            complete your reusable profile using the detected program from the extension
                        </p>
                    </div>

                    <div className="px-6 py-6 bg-white">
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4">
                                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">school</p>
                                <p className="text-sm font-semibold text-slate-900">
                                    {detectedProgram.school || 'not found'}
                                </p>
                            </div>

                            <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4">
                                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">program</p>
                                <p className="text-sm font-semibold text-slate-900">
                                    {detectedProgram.program}
                                </p>
                            </div>

                            <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4">
                                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">annual tuition</p>
                                <p className="text-sm font-bold text-[#1d4ed8]">
                                    ${detectedProgram.tuition.toLocaleString()}
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                            <p className="text-xs text-slate-400 mb-1">source</p>
                            <p className="text-xs text-slate-600 break-all">
                                {detectedProgram.title || detectedProgram.url}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="bg-[#1e293b] px-6 py-4 border-b border-slate-700">
                        <h2 className="text-white text-lg font-semibold">complete your profile</h2>
                        <p className="text-slate-300 text-sm mt-1">
                            after this, future tuition pages can go straight to the final calculation
                        </p>
                    </div>

                    <div className="px-2 py-6 md:px-4">
                        <CombinedWizard
                            onComplete={handleComplete}
                            lockStudyInfo={true}
                            presetStudyInfo={{
                                school: detectedProgram.school || '',
                                program: detectedProgram.program,
                                annualTuition: detectedProgram.tuition,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}